import { IParsedJSON, IParseFailure, IParseStatistic, ParseErrorCategory } from 'types/cvParserInterfaces'
import { IAccount } from 'types/userInterfaces'
import { SubAPI } from './API'

/**
 * API for Cv Parser
 * @notExported
 */
class CvParserAPI extends SubAPI {
  public async parseCV(data: FormData, controller?: AbortController): Promise<IParsedJSON[]> {
    return await this.api.post(`cv-parser/parse`, data, controller ? { signal: controller.signal } : undefined, {
      'Content-Type': 'multipart/form-data',
    })
  }

  /**
   * Start async CV parsing and returns job ID.
   *
   * @param data - FormData with files to parse
   * @param controller - Abort controller for cancellation
   * @returns Job ID for tracking parsing status
   */
  public async parseCVAsync(data: FormData, controller?: AbortController): Promise<{ jobId: string }> {
    return await this.api.post(`cv-parser/parse-async`, data, controller ? { signal: controller.signal } : undefined, {
      'Content-Type': 'multipart/form-data',
    })
  }

  /**
   * Get parsing job status
   *
   * @param jobId - Job ID from async parse request
   * @param controller - Abort controller for cancellation
   * @returns Job status with progress, and results and failed files if completed
   */
  public async getParseStatus(
    jobId: string,
    controller?: AbortController
  ): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'error'
    progress: number
    results?: IParsedJSON[]
    failures?: IParseFailure[]
    error?: string
    errorCategory?: ParseErrorCategory
  }> {
    return await this.api.get(`cv-parser/status/${jobId}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Saves parsed CV to the database.
   *
   * @param data - Parsed CV data.
   * @param saveDetails - Activation, site, organization and network visibility details.
   * @returns Created account and errors.
   */
  public async save(
    data: IParsedJSON[],
    saveDetails: {
      organizationId: number
      siteId: number
      activation: boolean
      networkVisible: boolean
    },
    controller?: AbortController
  ): Promise<{ account?: IAccount; errors: string[] }[]> {
    return await this.api.post(
      `cv-parser/save`,
      { data, details: saveDetails },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Saves parsed CV to the database and deletes source proposal upload on success.
   *
   * @param data - Parsed CV data.
   * @param saveDetails - Activation, site, organization and network visibility details.
   * @param uploadId - Source proposal upload ID to clean up after save.
   * @returns Created account/errors and cleanup status.
   */
  public async saveFromUpload(
    data: IParsedJSON,
    saveDetails: {
      organizationId: number
      siteId: number
      activation: boolean
      networkVisible: boolean
    },
    uploadId: number,
    controller?: AbortController
  ): Promise<{ result: { account?: IAccount; errors: string[] }; uploadDeleted: boolean }> {
    return await this.api.post(
      `cv-parser/save-from-upload`,
      { data, details: saveDetails, uploadId },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Sends single CV to the parser.
   *
   * @param data - CV file data.
   * @returns Parsed CV data.
   */
  public async parseSingleCV(data: FormData, controller?: AbortController): Promise<{ jobId: string }> {
    return await this.api.post(`cv-parser/parseSingle`, data, controller ? { signal: controller.signal } : undefined, {
      'Content-Type': 'multipart/form-data',
    })
  }

  /**
   * Starts async parsing of an already-uploaded proposal attachment.
   * The file is read from storage server-side using the uploadId.
   *
   * @param uploadId - ID of the Upload record for the attachment
   * @param organizationId - Organization owning the parse credits
   * @param siteId - Site for the new profile
   * @param controller - Abort controller for cancellation
   * @returns Job ID for polling status
   */
  public async parseFromProposal(
    uploadId: number,
    organizationId: number,
    siteId: number,
    controller?: AbortController
  ): Promise<{ jobId: string }> {
    return await this.api.post(
      `cv-parser/parse-from-proposal`,
      { uploadId, organizationId, siteId },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Gets amount of parse credits left for organization.
   *
   * @param organizationId - Organization ID.
   * @returns Number of parse credits left.
   */
  public async getParseCredits(organizationId: number, controller?: AbortController): Promise<number> {
    return await this.api.get(
      `cv-parser/credits/${organizationId}`,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Gets CV parsing statistics for all organizations.
   *
   * @param controller - Abort controller for cancellation.
   * @return CV parsing statistics for all organizations.
   */
  public async getStatistics(controller?: AbortController): Promise<IParseStatistic[]> {
    return await this.api.get(`cv-parser/statistics`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Gets CV parsing statistics for a specific organization.
   *
   * @param organizationId - Organization ID.
   * @param controller - Abort controller for cancellation.
   * @returns CV parsing statistics for the specified organization.
   */
  public async getOrganizationStatistics(
    organizationId: number,
    controller?: AbortController
  ): Promise<IParseStatistic[]> {
    return await this.api.get(
      `cv-parser/statistics/${organizationId}`,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Translate CV content to the other existing CV language using Azure AI translation.
   * @param cvId - CV ID.
   * @param language - Current language code.
   * @returns Updated full CV data.
   */
  public async translateCV(cvId: number, language: string, controller?: AbortController): Promise<{ jobId: string }> {
    return await this.api.post(
      `cv-parser/translate/${cvId}`,
      { language },
      controller ? { signal: controller.signal } : undefined
    )
  }
}

export const cvParserAPI = new CvParserAPI()
